// workspaces.ts — mapa nawigacji Control Center.
//
// Model (R5-15 §18): górny pasek przełącza PRZESTRZENIE ROBOCZE, lewy pasek pokazuje funkcje
// wewnątrz jednej przestrzeni. Ani „każdy ekran to osobna aplikacja”, ani „jeden wielki komponent”.
//
// Uprawnienia przy pozycjach służą WYŁĄCZNIE do chowania nawigacji (UX). Autoryzacja jest
// po stronie backendu — patrz nagłówek permissions.ts.

import type { Permission } from '@/lib/auth/permissions';

/** Dojrzałość ekranu — pokazywana użytkownikowi, żeby nie brał szkicu za gotowe narzędzie. */
export type SectionMaturity =
  | 'live' // działa na realnym API
  | 'planned' // celowo pusty, zaprojektowany, jeszcze nie zbudowany
  | 'blocked'; // zbudowanie zablokowane przez udowodniony blocker (opis w `note`)

export interface WorkspaceSection {
  id: string;
  /** Ścieżka względem przestrzeni roboczej. Pusty string = ekran domyślny. */
  path: string;
  label: string;
  maturity: SectionMaturity;
  permissions?: readonly Permission[];
  note?: string;
}

export interface Workspace {
  id: string;
  path: string;
  label: string;
  sections: readonly WorkspaceSection[];
}

// ŚWIADOMA DECYZJA: górny pasek pokazuje CAŁĄ mapę produktu, także przestrzenie, w których
// dana osoba nie zobaczy jeszcze danych. Ekrany bez danych (zaplanowane/zablokowane) nie mają
// uprawnień, bo nie mają czego chronić — mówią wyłącznie, czego jeszcze nie ma i dlaczego.
// Uprawnienia siedzą tam, gdzie są DANE. Przestrzeń znika z paska dopiero wtedy, gdy żadna
// jej sekcja nie jest dla tej osoby widoczna.

export const WORKSPACES: readonly Workspace[] = [
  {
    id: 'home',
    path: '/',
    label: 'Start',
    sections: [{ id: 'overview', path: '', label: 'Przegląd', maturity: 'live' }],
  },
  {
    id: 'admin',
    path: '/admin',
    label: 'Administracja',
    sections: [
      {
        id: 'users',
        path: '',
        label: 'Użytkownicy',
        maturity: 'blocked',
        note: 'Wymaga zunifikowanej tożsamości (ADR-003). Dziś konta operatorów CRM pochodzą ze zmiennych środowiskowych, a panel główny ma własny, niezależny system logowania.',
      },
      {
        id: 'schools',
        path: 'schools',
        label: 'Szkoły i restauracje',
        maturity: 'planned',
        note: 'Ekran migrowany z panelu legacy. Inwentaryzacja ekranów i zapisów jest w 02_Legacy_Inventory.',
      },
      {
        id: 'grafik',
        path: 'grafik',
        label: 'Grafik',
        maturity: 'live',
        // Grafik to konfiguracja DOSTAW (kto, gdzie, w jakie dni), a nie sprzedaż — stąd
        // `analytics.delivery.read`, a nie `analytics.sales.read`. Nie zakładamy tu osobnego
        // uprawnienia: rozszerzenie modelu ról to zmiana, która musi mieć własny powód,
        // a nie dziać się mimochodem przy dokładaniu ekranu.
        permissions: ['analytics.delivery.read'],
        note: 'Kto dowozi, do której szkoły, w jakie dni i do której godziny można zamawiać. Widok tylko do odczytu — replika systemu głównego.',
      },
      {
        id: 'organizacja',
        path: 'organizacja',
        label: 'Organizacja',
        maturity: 'live',
        // Trzy ekrany, trzy pytania: Szkoły = analityka, Grafik = harmonogram,
        // Organizacja = relacja. Zlanie ich dałoby ekran bez żadnej z tych odpowiedzi.
        permissions: ['analytics.delivery.read'],
        note: 'Która restauracja obsługuje którą szkołę. Odrębne od Grafiku i od Szkół.',
      },
      {
        id: 'menu-moderacja',
        path: 'menu',
        label: 'Moderacja menu',
        maturity: 'live',
        permissions: ['restaurant.menu.read'],
        note: 'Kolejka dań czekających na decyzję. Ścieżka ręczna zostaje dostępna także po włączeniu moderacji automatycznej.',
      },
    ],
  },
  {
    id: 'restaurants',
    path: '/restauracje',
    label: 'Restauracje',
    sections: [
      {
        id: 'sales',
        path: '',
        label: 'Sprzedaż',
        maturity: 'live',
        permissions: ['analytics.sales.read'],
      },
      {
        id: 'dishes',
        path: 'dania',
        label: 'Dania',
        maturity: 'live',
        permissions: ['analytics.sales.read'],
      },
      {
        id: 'schools',
        path: 'szkoly',
        label: 'Szkoły',
        maturity: 'live',
        permissions: ['analytics.sales.read'],
      },
      {
        id: 'catalog',
        path: 'katalog',
        label: 'Katalog dań',
        maturity: 'live',
        permissions: ['analytics.sales.read'],
      },
      {
        id: 'menu',
        path: 'menu',
        label: 'Menu',
        maturity: 'planned',
      },
    ],
  },
  {
    /*
     * OSOBNY OBSZAR, NIE SEKCJA W „RESTAURACJE".
     *
     * „Restauracje" to ANALITYKA — ile która sprzedała, co się sprzedaje, jaki katalog.
     * „Panel restauracji" to WIDOK OPERACYJNY jednej restauracji: co dziś ugotować i dokąd
     * to jedzie. Zlanie tych dwóch powtórzyłoby pomyłkę, którą właśnie rozplątaliśmy przy
     * Szkołach, Grafiku i Organizacji.
     */
    id: 'restaurant-panel',
    path: '/panel-restauracji',
    label: 'Panel restauracji',
    sections: [
      {
        id: 'dzisiaj',
        path: '',
        label: 'Dzisiejsze zamówienia',
        maturity: 'live',
        permissions: ['restaurant.sales.read'],
        note: 'Co i ile ugotować, i do której szkoły to jedzie. Ekran roboczy kuchni.',
      },
      {
        id: 'dania',
        path: 'dania',
        // „Dania" istnieje już pod Restauracjami jako ANALITYKA sprzedaży dań. Dwa te same
        // napisy w nawigacji mylą i operatora, i paletę poleceń. To jest KARTA dań.
        label: 'Karta dań',
        maturity: 'live',
        // Karta dań to zdolność restauracyjna, nie analityczna — stąd `restaurant.menu.read`,
        // a nie `analytics.sales.read` jak w analitycznym ekranie „Dania" pod Restauracjami.
        permissions: ['restaurant.menu.read'],
        note: 'Karta dań restauracji. Każda zmiana przechodzi przez moderację.',
      },
      {
        id: 'szkoly',
        path: 'szkoly',
        label: 'Szkoły',
        maturity: 'live',
        permissions: ['restaurant.sales.read'],
        note: 'Tylko do odczytu. Przypisania i dni dostaw zmienia się w Administracja → Organizacja.',
      },
      {
        id: 'raporty-dostaw',
        path: 'raporty-dostaw',
        label: 'Raporty dostaw',
        maturity: 'live',
        // Dane systemu kierowców, ale pytanie jest restauracyjne („czy MOJE dowozy są na czas"),
        // stąd uprawnienie dostaw, a nie sprzedaży.
        permissions: ['analytics.delivery.read'],
        note: 'Punktualność, kierowcy i trafność zgłoszeń spóźnień — dla tej restauracji.',
      },
      {
        id: 'transakcje',
        path: 'transakcje',
        // „Transakcje" nosi już parytetowy ekran CRM. Dwa te same napisy w szynie i w palecie
        // poleceń zmuszają operatora do zgadywania, w który wchodzi — trzecie takie starcie
        // po „Menu" i „Daniach". Ten ekran jest HISTORIĄ, więc nazwa mówi to wprost.
        label: 'Historia transakcji',
        maturity: 'live',
        permissions: ['restaurant.sales.read'],
        note: 'Historia sprzedanych pozycji tej restauracji. Wartość katalogowa, nie rozliczenie.',
      },
    ],
  },
  {
    id: 'crm',
    path: '/crm',
    label: 'CRM',
    sections: [
      {
        id: 'school-sales',
        path: '',
        label: 'Sprzedaż wg szkół',
        maturity: 'live',
        permissions: ['analytics.sales.read'],
      },
      {
        id: 'transactions',
        path: 'transakcje',
        label: 'Transakcje',
        maturity: 'live',
        permissions: ['analytics.sales.read'],
      },
      {
        id: 'opinie',
        path: 'opinie',
        label: 'Opinie',
        maturity: 'live',
        permissions: ['analytics.sales.read'],
        note: 'Oceny uczniów wystawione daniom. Siedem kolumn, wszystkie filtrowalne, żadna sortowalna — dokładnie jak w starym panelu.',
      },
      {
        id: 'campaigns',
        path: 'kampanie',
        label: 'Kampanie',
        maturity: 'planned',
        permissions: ['crm.campaigns.read'],
      },
    ],
  },
  {
    id: 'drive',
    path: '/drive',
    label: 'Dostawy',
    sections: [
      { id: 'deliveries', path: '', label: 'Punktualność', maturity: 'live' },
      { id: 'schools', path: 'szkoly', label: 'Wg szkół', maturity: 'live' },
      { id: 'history', path: 'historia', label: 'Historia', maturity: 'live' },
    ],
  },
  {
    id: 'insights',
    path: '/insights',
    label: 'Insighty',
    sections: [
      { id: 'all', path: '', label: 'Wszystkie sygnały', maturity: 'live', permissions: ['analytics.sales.read'] },
      {
        id: 'coincidence',
        path: 'powiazania',
        label: 'Powiązania',
        maturity: 'live',
        permissions: ['analytics.sales.read'],
      },
    ],
  },
  {
    id: 'reports',
    path: '/raporty',
    label: 'Raporty',
    sections: [{ id: 'reports', path: '', label: 'Eksporty', maturity: 'planned' }],
  },
  {
    id: 'system',
    path: '/system',
    label: 'System',
    sections: [
      { id: 'status', path: '', label: 'Status połączeń', maturity: 'live' },
      /*
       * Gotowość i Jakość danych świadomie stoją w przestrzeni System, a nie w „ustawieniach
       * zaawansowanych". Pytanie „czy mogę na tej liczbie oprzeć decyzję" pada równie często
       * jak „ile sprzedaliśmy" — a odpowiedź na pierwsze rozstrzyga, czy druga jest cokolwiek
       * warta. Schowanie ich głębiej znaczyłoby, że to szczegół techniczny. Nie jest.
       */
      { id: 'readiness', path: 'gotowosc', label: 'Gotowość', maturity: 'live' },
      { id: 'quality', path: 'jakosc', label: 'Jakość danych', maturity: 'live' },
      {
        id: 'audit',
        path: 'audyt',
        label: 'Dziennik zmian',
        maturity: 'blocked',
        note: 'Centralnego dziennika zmian jeszcze nie ma. Kontrakt docelowy: 04_Contracts/audit-contract-v0.',
      },
    ],
  },
];

export function findWorkspace(id: string): Workspace | undefined {
  return WORKSPACES.find((w) => w.id === id);
}

/** Sekcje widoczne dla danego zestawu uprawnień. Chowanie nawigacji, nie autoryzacja. */
export function visibleSections(
  workspace: Workspace,
  isVisible: (permissions?: readonly Permission[]) => boolean,
): WorkspaceSection[] {
  return workspace.sections.filter((s) => isVisible(s.permissions));
}

/** Pełna ścieżka routingu dla sekcji. */
export function sectionPath(workspace: Workspace, section: WorkspaceSection): string {
  if (workspace.path === '/') return section.path ? `/${section.path}` : '/';
  return section.path ? `${workspace.path}/${section.path}` : workspace.path;
}
